import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import { useStores, useFocus, useSearchInput } from 'hooks';
import { Icon } from 'components/project/ClerkScreen';
import { clerkColors } from 'shared/utils/theme';
import NoResultsSVG from './NoResultsSvg';
import { ScrollToBottom } from './ScrollToBottom';
import {
  ItemSearch,
  SearchInputCont,
  SearchInput,
  SearchIcon,
  SearchSpinner,
  Item,
  ItemData,
  ItemListContainer,
  ItemList,
  ItemTitle,
  SectionTitle,
  NoResults,
  NoResultsTitle,
  NoResultsTip,
  FilterButton,
  FilterButtonContainer,
} from './Styles';

const BidActivity = () => {
  const {
    MessageStore: { bidActivityMappings, auctionLogObject: data },
    BidStore: { auction },
    WebSocketStore: { sendFocusSidePanelSearchMessage },
  } = useStores();

  const searchChangeDataMatcher = (searchData, searchTerm) => searchData.filter(
    (item) => item.message.toUpperCase().indexOf(searchTerm.toUpperCase()) > -1,
  );

  const filterClickDataMatcher = (filterData, filterValue) => filterData.filter((item) => item.type === filterValue);

  const {
    setIsLoading,
    isLoading,
    isSearchTermEmpty,
    filteredData,
    events: { handleFilterClick, handleSearchChange },
  } = useSearchInput(searchChangeDataMatcher, { data, filterClickDataMatcher });

  const [searchInputRef, setSearchInputRefFocus] = useFocus();

  useEffect(() => {
    if (auction.focusOnSidePanelSearch) {
      setSearchInputRefFocus();
    }

    setIsLoading(false);
  }, [setIsLoading, setSearchInputRefFocus, auction.focusOnSidePanelSearch]);

  const filterButtonMappings = [
    {
      bgColor: '#e7e7e7',
      bgVariance: -20,
      color: '#000000',
      icon: 'times',
      text: '',
      value: null,
    },
    {
      bgColor: '#e7e7e7',
      bgVariance: -20,
      color: '#000000',
      text: 'General',
      value: 0,
    },
    {
      bgColor: '#e7e7e7',
      bgVariance: -20,
      color: '#000000',
      text: 'Block Change',
      value: 4,
    },
    {
      bgColor: '#e7e7e7',
      bgVariance: -20,
      color: '#000000',
      text: 'Lane Status',
      value: 3,
    },
    {
      bgColor: '#e7e7e7',
      bgVariance: 50,
      color: '#ff0000',
      text: 'Errors',
      value: -1,
    },
    {
      bgColor: '#e7e7e7',
      bgVariance: 20,
      color: '#0095E8',
      text: 'Online Bids',
      value: 1,
    },
    {
      bgColor: '#e7e7e7',
      bgVariance: 20,
      color: '#21BA45',
      text: 'Floor Bids',
      value: 2,
    },
    {
      bgColor: '#21BA45',
      bgVariance: 20,
      color: '#FFFFFF',
      text: 'Sold',
      value: 6,
    },
    {
      bgColor: '#FBCC08',
      bgVariance: 20,
      color: '#000000',
      text: 'Subject To',
      value: 7,
    },
    {
      bgColor: '#DB2828',
      bgVariance: 50,
      color: '#FFFFFF',
      text: 'No Sale',
      value: 5,
    },
  ];

  return (
    <ItemSearch>
      <SearchInputCont>
        <SearchInput
          autoFocus
          placeholder="Search bid activity by message"
          ref={searchInputRef}
          onChange={handleSearchChange}
          onFocus={() => sendFocusSidePanelSearchMessage()}
        />
        <SearchIcon type="search" size={22} />
        {isLoading && <SearchSpinner />}
      </SearchInputCont>

      {filterButtonMappings && (
        <FilterButtons
          mappings={filterButtonMappings}
          onClick={handleFilterClick}
        />
      )}

      {!isLoading && filteredData.length > 0 && (
        <>
          <SectionTitle>
            Bid Activity (
            {filteredData.length}
            )
          </SectionTitle>
          <ItemListContainer>
            <ItemList>
              <RenderItem
                data={filteredData}
                colors={clerkColors}
                mappings={bidActivityMappings}
              />
              {filteredData.length && <ScrollToBottom />}
            </ItemList>
          </ItemListContainer>
        </>
      )}

      {!isSearchTermEmpty && !isLoading && filteredData.length === 0 && (
        <NoResults>
          <NoResultsSVG />
          <NoResultsTitle>
            We couldn&apos;t find anything matching your search
          </NoResultsTitle>
          <NoResultsTip>Try again with a different item.</NoResultsTip>
        </NoResults>
      )}
      
    </ItemSearch>
  );
};

export const FilterButtons = ({ mappings, onClick }) => (
  <FilterButtonContainer>
    {mappings.map((item) => (
      <FilterButton
        key={item.value}
        iconOnly
        data-testid={`filter-btn-${item.value}`}
        bgColor={item.bgColor}
        color={item.color}
        bgVariance={item.bgVariance}
        onClick={() => onClick(item.value)}
      >
        {item.icon && <Icon icon={item.icon} />}
        {item?.text}
      </FilterButton>
    ))}
  </FilterButtonContainer>
);

FilterButtons.propTypes = {
  mappings: PropTypes.arrayOf(PropTypes.shape({
    bgColor: PropTypes.string.isRequired,
    bgVariance: PropTypes.number.isRequired,
    color: PropTypes.string.isRequired,
    icon: PropTypes.string,
    text: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  })).isRequired,
  onClick: PropTypes.func.isRequired,
};

const RenderItem = ({ data, colors, mappings }) => {
  const getAttributesForItem = (id) => {
    const mapping = mappings.filter((item) => item.messageType === id)[0];

    return {
      icon: mapping.icon,
      bgColor: colors[mapping.bgColor],
      color: colors[mapping.color],
      hoverVariance: mapping.hoverVariance,
    };
  };

  return data.map((item, index) => {
    const {
      color, bgColor, hoverVariance, icon,
    } = getAttributesForItem(
      item.type,
    );
    return (
      <Item
        color={color}
        bgColor={bgColor}
        hoverVariance={hoverVariance}
        key={index}
      >
        <Icon icon={icon} />
        <ItemData>
          <ItemTitle color={color}>{item.message}</ItemTitle>
        </ItemData>
      </Item>
    );
  });
};

export default observer(BidActivity);
